import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { useSiteSettings } from "@/contexts/SiteSettingsContext";
import { MagnifyingGlass, MapPin, Lightning, ShieldCheck, Drop, Wrench, Factory, CarProfile, Boat, Fire, Flask, Buildings, ArrowRight, CheckCircle, PlayCircle, Quotes, Anchor } from "@phosphor-icons/react";

const DEFAULT_HERO_IMG = "https://images.unsplash.com/photo-1581094794329-c8112a89af12?auto=format&fit=crop&w=1800&q=80";

const ICON_MAP = {
  car: CarProfile, factory: Factory, wrench: Wrench, fire: Fire, anchor: Anchor, boat: Boat,
  building: Buildings, flask: Flask, drop: Drop, shield: ShieldCheck, lightning: Lightning,
};
function FeatureIcon({ keyName, ...rest }) {
  const Cmp = ICON_MAP[keyName] || Lightning;
  return <Cmp {...rest} />;
}

const WHY_POINTS = [
  { icon: Drop, label: "No Water", desc: "No slurry, no runoff. Works on electrical gear and sensitive machinery." },
  { icon: ShieldCheck, label: "No Chemicals", desc: "No solvents. Safe for food production, hospitals, museums." },
  { icon: Lightning, label: "No Residue", desc: "Dry ice sublimates into air. Only the removed dirt remains." },
  { icon: Wrench, label: "Non-Abrasive", desc: "Won't damage paint, wiring, substrates or stamped parts." },
];

const FAQS = [
  { q: "What is dry ice blasting?", a: "A cleaning process that uses solid CO₂ pellets accelerated by compressed air. The pellets sublimate on contact — turning from solid to gas — lifting dirt and contaminants away without water, chemicals or secondary waste." },
  { q: "Will it damage my equipment, car or surface?", a: "No. Dry ice is softer than most substrates — it's non-abrasive on paint, wiring, rubber, aluminum, and even delicate historic surfaces. A certified contractor will match pellet size and pressure to your job." },
  { q: "How much does a dry ice blasting job cost?", a: "Pricing varies by job size, complexity and location. Use the map above to contact contractors near you for free quotes — Dry Ice Connect charges no fee and takes no commission." },
  { q: "Is it safe indoors?", a: "Yes, when done properly with ventilation. CO₂ levels are monitored by every pro contractor. Explain your site conditions to the contractor when requesting a quote." },
  { q: "How do I pick a contractor?", a: "Check the service type tags (car, industrial), mobile vs in-shop, distance, and the contractor's profile photos & videos. Message two or three for quotes." },
];

export default function Home() {
  const s = useSiteSettings();
  const [zip, setZip] = useState("");
  const navigate = useNavigate();

  const search = (e) => {
    e?.preventDefault?.();
    const z = zip.trim();
    if (!z) return;
    navigate(`/find?zip=${encodeURIComponent(z)}`);
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      navigate("/find");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => navigate(`/find?lat=${pos.coords.latitude}&lng=${pos.coords.longitude}`),
      () => navigate("/find"),
    );
  };

  return (
    <Layout>
      {/* HERO + SEARCH */}
      <section className="relative overflow-hidden border-b border-[#262626]" data-testid="customer-hero">
        <div className="absolute inset-0 bg-grid bg-grid-fade opacity-30" />
        <div className="absolute inset-0">
          <img src={s.hero_image_url || DEFAULT_HERO_IMG} alt="" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/60 via-[#0A0A0A]/85 to-[#0A0A0A]" />
        </div>

        <div className="relative max-w-[1400px] mx-auto px-4 lg:px-8 pt-16 pb-12 lg:pt-24 lg:pb-16">
          <div className="flex items-center gap-3 mb-6 justify-center">
            <div className="h-px w-12 bg-[#00D4FF]" />
            <span className="section-label">{s.hero_label || "— NATIONWIDE DIRECTORY · NO COMMISSION"}</span>
            <div className="h-px w-12 bg-[#00D4FF]" />
          </div>
          <h1 className="font-chivo font-black text-4xl sm:text-5xl lg:text-7xl tracking-tighter leading-[0.95] uppercase text-white text-center" data-testid="customer-hero-headline">
            {s.hero_title || "Find a dry ice blasting pro"}<br /><span className="text-[#00D4FF]">{s.hero_title_highlight || "near you."}</span>
          </h1>
          <p className="mt-6 font-plex text-base sm:text-lg text-[#9CA3AF] max-w-2xl mx-auto text-center">
            {s.hero_subtitle || "Car detailers, industrial cleaning, mobile or in-shop. Contact contractors directly. No middleman, no fees."}
          </p>

          {/* Zip search */}
          <form onSubmit={search} className="mt-10 max-w-2xl mx-auto surface p-4 lg:p-5 flex flex-col sm:flex-row gap-3" data-testid="zip-search-form">
            <div className="flex-1 flex items-center gap-3 input-rugged py-3">
              <MapPin size={20} weight="duotone" className="text-[#00D4FF] shrink-0" />
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={5}
                placeholder="Enter your zip code"
                value={zip}
                onChange={(e) => setZip(e.target.value.replace(/\D/g, ""))}
                className="flex-1 bg-transparent outline-none text-base text-white placeholder-[#52525B]"
                data-testid="zip-input"
              />
            </div>
            <button type="submit" className="btn-primary" data-testid="search-zip-btn">
              <MagnifyingGlass size={16} weight="bold" /> Find A Blaster
            </button>
            <button type="button" onClick={useMyLocation} className="btn-secondary" data-testid="use-location-btn">
              Use My Location
            </button>
          </form>

          {/* Two-door strip */}
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl mx-auto">
            <Link to="/find" className="surface surface-hover p-4 group flex items-center justify-between" data-testid="door-customer">
              <div>
                <div className="font-mono text-[10px] text-[#00D4FF] tracking-widest uppercase">NEED A BLASTER?</div>
                <div className="font-chivo font-bold text-white mt-1">Search the map</div>
              </div>
              <ArrowRight size={18} className="text-[#52525B] group-hover:text-[#00D4FF]" weight="bold" />
            </Link>
            <Link to="/pros/join" className="surface surface-hover p-4 group flex items-center justify-between" data-testid="door-contractor">
              <div>
                <div className="font-mono text-[10px] text-[#00D4FF] tracking-widest uppercase">OWN A BLASTER?</div>
                <div className="font-chivo font-bold text-white mt-1">Join Dry Ice Connect</div>
              </div>
              <ArrowRight size={18} className="text-[#52525B] group-hover:text-[#00D4FF]" weight="bold" />
            </Link>
          </div>
        </div>
      </section>

      {/* MAP PREVIEW */}
      <section className="max-w-[1400px] mx-auto px-4 lg:px-8 py-14" data-testid="map-preview-section">
        <div className="section-label mb-3">{s.map_preview_label || "— THE NETWORK"}</div>
        <h2 className="font-chivo font-black text-3xl sm:text-4xl text-white uppercase tracking-tight mb-6">{s.map_preview_title || "The US map of pro blasters."}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {[
            { city: "Los Angeles", state: "CA", zip: "90001" },
            { city: "Dallas", state: "TX", zip: "75201" },
            { city: "Chicago", state: "IL", zip: "60601" },
            { city: "Miami", state: "FL", zip: "33101" },
            { city: "Pittsburgh", state: "PA", zip: "15213" },
            { city: "Your Zip", state: "US", zip: "" },
          ].map((c) => (
            <Link
              key={c.zip || "any"}
              to={c.zip ? `/find?zip=${c.zip}&radius=100` : "/find"}
              className="surface surface-hover p-5 group flex items-center justify-between"
              data-testid={`map-quick-${c.zip || "any"}`}
            >
              <div>
                <div className="font-mono text-[10px] text-[#00D4FF] tracking-widest uppercase">BROWSE PROS</div>
                <div className="font-chivo font-bold text-white mt-1">{c.city}, {c.state}</div>
              </div>
              <ArrowRight size={16} className="text-[#52525B] group-hover:text-[#00D4FF]" weight="bold" />
            </Link>
          ))}
        </div>
      </section>

      {/* WHY DRY ICE */}
      <section className="border-y border-[#262626] bg-[#121212]">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-10">
            <div className="lg:col-span-5">
              <div className="section-label mb-3">{s.why_label || "— WHY DRY ICE"}</div>
              <h2 className="font-chivo font-black text-3xl sm:text-4xl lg:text-5xl text-white uppercase tracking-tight leading-none">
                {(s.why_title || "No water. No chemicals.").split(".").filter(Boolean).map((piece, idx) => (
                  <span key={idx}>{piece}.{idx < (s.why_title || "No water. No chemicals.").split(".").filter(Boolean).length - 1 ? <br/> : null}</span>
                ))}<br /><span className="text-[#00D4FF]">{s.why_title_highlight || "No residue."}</span>
              </h2>
            </div>
            <div className="lg:col-span-7 lg:pt-6">
              <p className="font-plex text-base text-[#9CA3AF] leading-relaxed">
                {s.why_subtitle || "Dry ice blasting uses solid CO₂ pellets accelerated by compressed air. The pellets sublimate on contact — turning from solid to gas — lifting dirt away without secondary waste."}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#262626] border border-[#262626]">
            {WHY_POINTS.map((w) => (
              <div key={w.label} className="bg-[#121212] p-6" data-testid={`why-${w.label.toLowerCase().replace(/\s+/g, "-")}`}>
                <w.icon size={28} weight="duotone" className="text-[#00D4FF] mb-4" />
                <div className="font-chivo font-bold text-white mb-2">{w.label}</div>
                <p className="font-plex text-sm text-[#9CA3AF] leading-relaxed">{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST LOGOS */}
      {(s.trust_logos || []).length > 0 && (
        <section className="border-y border-[#262626] bg-[#0A0A0A]" data-testid="trust-logos-section">
          <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-10">
            <div className="section-label mb-4 text-center">{s.trust_logos_label || "— AS SEEN IN"}</div>
            <div className="flex flex-wrap items-center justify-center gap-6 lg:gap-12 opacity-80" data-testid="trust-logos">
              {s.trust_logos.map((l) => (
                l.link_url ? (
                  <a key={l.id} href={l.link_url} target="_blank" rel="noopener noreferrer" className="hover:opacity-100 transition-opacity">
                    <img src={l.image_url} alt={l.title || "partner"} className="h-10 lg:h-12 object-contain grayscale hover:grayscale-0" data-testid={`trust-logo-${l.id}`} />
                  </a>
                ) : (
                  <img key={l.id} src={l.image_url} alt={l.title || "partner"} className="h-10 lg:h-12 object-contain grayscale hover:grayscale-0" data-testid={`trust-logo-${l.id}`} />
                )
              ))}
            </div>
          </div>
        </section>
      )}

      {/* USE CASES (dynamic features) */}
      <section className="max-w-[1400px] mx-auto px-4 lg:px-8 py-16" data-testid="use-cases">
        <div className="section-label mb-3">{s.use_cases_label || "— USE CASES"}</div>
        <h2 className="font-chivo font-black text-3xl sm:text-4xl text-white uppercase tracking-tight mb-10">{s.use_cases_title || "Where dry ice blasting shines."}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {(s.features || []).map((u) => (
            <div key={u.id} className="surface p-6" data-testid={`use-case-${u.id}`}>
              <FeatureIcon keyName={u.icon_key} size={32} weight="duotone" className="text-[#00D4FF] mb-4" />
              <div className="font-chivo font-bold text-white text-lg mb-2">{u.title}</div>
              <p className="font-plex text-sm text-[#9CA3AF] leading-relaxed">{u.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      {(s.testimonials || []).length > 0 && (
        <section className="bg-[#121212] border-y border-[#262626]" data-testid="testimonials-section">
          <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-16">
            <div className="section-label mb-3">{s.testimonials_label || "— TRUSTED BY CONTRACTORS"}</div>
            <h2 className="font-chivo font-black text-3xl sm:text-4xl text-white uppercase tracking-tight mb-10">{s.testimonials_title || "What our pros say."}</h2>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {s.testimonials.map((t) => (
                <figure key={t.id} className="surface p-6" data-testid={`testimonial-${t.id}`}>
                  <Quotes size={28} weight="fill" className="text-[#00D4FF] mb-3" />
                  <blockquote className="font-plex text-sm text-white leading-relaxed">{t.body}</blockquote>
                  <figcaption className="flex items-center gap-3 mt-4 pt-4 border-t border-[#262626]">
                    {t.image_url && <img src={t.image_url} alt="" className="w-10 h-10 object-cover" />}
                    <div>
                      <div className="font-chivo font-bold text-white text-sm">{t.author}</div>
                      <div className="font-mono text-xs text-[#52525B]">{t.company}</div>
                    </div>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* VIDEO PLACEHOLDER */}
      <section className="bg-[#121212] border-y border-[#262626]">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-16 grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
          <div className="lg:col-span-5">
            <div className="section-label mb-3">{s.video_label || "— SEE IT IN ACTION"}</div>
            <h2 className="font-chivo font-black text-3xl sm:text-4xl text-white uppercase tracking-tight leading-none">
              {s.video_title || "Watch real jobs."}<br /><span className="text-[#00D4FF]">{s.video_title_highlight || "Real results."}</span>
            </h2>
            <p className="font-plex text-sm text-[#9CA3AF] mt-4 max-w-md">
              {s.video_subtitle || "Before/after videos from certified contractors. Automotive, industrial, restoration — see what dry ice can do on your kind of job."}
            </p>
            <Link to="/videos" className="btn-secondary text-xs mt-6 inline-flex" data-testid="watch-videos-cta">
              <PlayCircle size={14} weight="bold" /> Open Video Library
            </Link>
          </div>
          <div className="lg:col-span-7">
            {s.video_url ? (
              <div className="aspect-video border border-[#262626] overflow-hidden" data-testid="home-video">
                <iframe src={s.video_url.replace("watch?v=", "embed/")} title="Video" className="w-full h-full" allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen />
              </div>
            ) : (
              <div className="aspect-video bg-[#0A0A0A] border border-dashed border-[#262626] flex items-center justify-center">
                <div className="text-center p-6">
                  <PlayCircle size={56} weight="duotone" className="text-[#00D4FF] mx-auto mb-3" />
                  <div className="font-mono text-xs text-[#52525B] tracking-widest uppercase">Video gallery launching soon</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 lg:px-8 py-16" data-testid="faq-section">
        <div className="section-label mb-3">— FAQ</div>
        <h2 className="font-chivo font-black text-3xl sm:text-4xl text-white uppercase tracking-tight mb-8">Questions people ask.</h2>
        <div className="divide-y divide-[#262626] border-y border-[#262626]">
          {FAQS.map((f, idx) => (
            <details key={idx} className="group py-5" data-testid={`faq-${idx}`}>
              <summary className="cursor-pointer font-chivo font-bold text-white text-base sm:text-lg uppercase tracking-tight flex items-center justify-between gap-4">
                <span>{f.q}</span>
                <span className="font-mono text-[#00D4FF] text-xl group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="font-plex text-sm text-[#9CA3AF] leading-relaxed mt-3">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* BOTTOM STRIP: for contractors */}
      <section className="border-t border-[#262626] bg-[#121212]">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-14 grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
          <div className="md:col-span-8">
            <div className="section-label mb-3">{s.bottom_label || "— FOR CONTRACTORS"}</div>
            <h3 className="font-chivo font-black text-2xl sm:text-3xl text-white uppercase tracking-tight">
              {s.bottom_title || "Own a dry ice blaster?"} <span className="text-[#00D4FF]">{s.bottom_title_highlight || "Get listed on the map."}</span>
            </h3>
            <p className="font-plex text-sm text-[#9CA3AF] mt-3 max-w-2xl">
              {s.bottom_subtitle || "Cheaper pellets. Cheaper machines & accessories. Training, downloads, private support. Level 2 members get a profile on the customer map and direct inbound leads — no commission, ever."}
            </p>
          </div>
          <div className="md:col-span-4 flex md:justify-end gap-3">
            <Link to="/pros/join" className="btn-secondary" data-testid="bottom-pros-cta">See Plans</Link>
            <Link to="/register" className="btn-primary" data-testid="bottom-join-cta">Join Now</Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
